/* eslint-disable implicit-arrow-linebreak */
/* eslint-disable no-shadow */
/* eslint-disable no-unused-expressions */
/* eslint-disable indent */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Loading,
  Button,
  Icon,
  Callout,
  Select,
} from '@folio/stripes-components';
import { useStripes } from '@folio/stripes/core';
import {
  useAuthoritySourceFiles,
  useFacets,
  useAuthoritySuggestions,
  useAuthorityCreation,
} from '../../hooks';
import {
  isAutoCreateEnabled,
  getAuthoritySourceForType,
  getAuthorityTypeForFieldPath,
} from '../../utils/authoritySettings';
import SuggestionItem from './SuggestionItem';
import SelectorComponent from './SelectorComponent';
import css from './AuthoritySuggestions.css';

const propTypes = {
  fieldPath: PropTypes.string.isRequired,
  textValue: PropTypes.string.isRequired,
  isAuthorityField: PropTypes.bool.isRequired,
  hasAuthority: PropTypes.bool.isRequired,
  formValues: PropTypes.object.isRequired,
  arrayIndex: PropTypes.number.isRequired,
  controlledVocab: PropTypes.object.isRequired,
  contributorNameTypeId: PropTypes.string,
  authoritySettings: PropTypes.object,
  authoritySource: PropTypes.string,
  isAutoCreateEnabled: PropTypes.bool,
  onSelectSuggestion: PropTypes.func.isRequired,
  onTextValueChange: PropTypes.func,
  onHide: PropTypes.func.isRequired,
};

function AuthoritySuggestions({
  fieldPath,
  textValue,
  isAuthorityField,
  hasAuthority,
  formValues,
  arrayIndex,
  controlledVocab,
  contributorNameTypeId,
  authoritySettings,
  authoritySource,
  isAutoCreateEnabled,
  onSelectSuggestion,
  onTextValueChange,
  onHide,
}) {
  const [defaultSuggestionCount, setDefaultSuggestionCount] = useState(10);
  const [selectedSourceId, setSelectedSourceId] = useState('');
  const suggestionsRef = useRef(null);
  const calloutRef = useRef();
  const stripes = useStripes();

  const { createInstantAuthority, createAdvancedAuthority, isCreating } =
    useAuthorityCreation(null, authoritySettings);

  const {
    suggestions,
    isLoading,
    error,
    hasMore,
    loadMoreSuggestions,
    totalRecords,
    currentQuery,
    shouldShowCreationButtons,
  } = useAuthoritySuggestions(
    fieldPath,
    textValue,
    isAuthorityField,
    null,
    defaultSuggestionCount,
    selectedSourceId,
    formValues,
    arrayIndex,
    controlledVocab,
    contributorNameTypeId,
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        onHide();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onHide]);

  const handleSelectSuggestion = useCallback(
    (suggestion) => {
      onSelectSuggestion(suggestion);
    },
    [onSelectSuggestion],
  );

  const handleDefaultCountChange = useCallback((newCount) => {
    setDefaultSuggestionCount(newCount);
  }, []);

  const handleSourceFilterChange = useCallback((sourceId) => {
    setSelectedSourceId(sourceId);
  }, []);

  // Use authority settings passed from parent component
  const canCreateAuthority =
    stripes.hasPerm('ui-marc-authorities.authority-record.create') &&
    isAutoCreateEnabled;

  // Handle mouse down to prevent blur from text field before click
  const handleMouseDownCreate = useCallback((event, createFunction) => {
    event.preventDefault(); // Prevent blur on text field
    createFunction();
  }, []);

  // Prevent blur when clicking in non-interactive areas
  const handleContainerMouseDown = useCallback((event) => {
    const target = event.target;

    // Don't prevent default for select elements and their children
    if (target.tagName === 'SELECT' ||
        target.closest('select') ||
        target.closest('[role="combobox"]') ||
        target.closest('.stripes__select') ||
        target.matches('option')) {
      return; // Let select work normally
    }

    // Don't prevent default for buttons
    if (target.tagName === 'BUTTON' || target.closest('button')) {
      return; // Let buttons work normally
    }

    // Prevent blur for everything else (text, spans, divs, etc.)
    event.preventDefault();
  }, []);

  const handleInstantCreate = useCallback(async () => {
    if (!canCreateAuthority) {
      console.warn('Authority creation is disabled');
      return;
    }

    try {


      const newAuthority = await createInstantAuthority(
        textValue,
        fieldPath,
        formValues,
        arrayIndex,
        controlledVocab,
        authoritySource,
      );



      // التحقق الشامل من صحة البيانات
      if (!newAuthority) {
        throw new Error('Authority creation failed: No response received');
      }

      if (!newAuthority.id) {
        throw new Error('Authority creation failed: No ID returned');
      }

      if (!newAuthority.headingRef) {
        throw new Error('Authority creation failed: No heading reference');
      }

      // ربط التسجيلة الاستنادية
      onSelectSuggestion(newAuthority);

      calloutRef.current?.sendCallout({
        type: 'success',
        message: (
          <FormattedMessage
            id="ui-plugin-inventory-templates.authority.creation.success"
            defaultMessage="Authority record created and linked successfully"
          />
        ),
      });
    } catch (error) {
      console.error('Failed to create authority:', error);
      calloutRef.current?.sendCallout({
        type: 'error',
        message: (
          <FormattedMessage
            id="ui-plugin-inventory-templates.authority.creation.error"
            defaultMessage="Failed to create authority record: {error}"
            values={{ error: error.message }}
          />
        ),
      });
    }
  }, [
    canCreateAuthority,
    textValue,
    fieldPath,
    formValues,
    arrayIndex,
    controlledVocab,
    authoritySource,
    createInstantAuthority,
    onSelectSuggestion,
  ]);

  const handleAdvancedCreate = useCallback(() => {
    createAdvancedAuthority(
      textValue,
      fieldPath,
      formValues,
      arrayIndex,
      controlledVocab,
      authoritySource,
    );
  }, [
    textValue,
    fieldPath,
    formValues,
    arrayIndex,
    controlledVocab,
    authoritySource,
    createAdvancedAuthority,
  ]);

  const { facets } = useFacets({
    query: currentQuery || '',
    selectedFacets: ['sourceFileId'],
  });
  const { getSourceFileNameById } = useAuthoritySourceFiles();
  const intl = useIntl();

  const countOptions = [
    { value: 10, label: '10' },
    { value: 15, label: '15' },
    { value: 20, label: '20' },
    { value: 25, label: '25' },
    { value: 30, label: '30' },
    { value: 40, label: '40' },
    { value: 50, label: '50' },
    { value: 75, label: '75' },
    { value: 100, label: '100' },
  ];

  const getLoadMoreIncrement = () => {
    if (defaultSuggestionCount >= 50) {
      return 25;
    } else if (defaultSuggestionCount >= 30) {
      return 20;
    } else {
      return defaultSuggestionCount;
    }
  };

  const hasValidSuggestions = !isLoading && !error && suggestions.length > 0;
  const shouldShowNoResults =
    !isLoading && !error && shouldShowCreationButtons && textValue.length >= 3;

  // Separate condition for showing creation buttons
  const shouldShowCreationButtonsInNoResults =
    shouldShowNoResults && canCreateAuthority;

  return (
    <div
      ref={suggestionsRef}
      className={css.suggestionsContainer}
      data-testid="authority-suggestions"
      onMouseDown={handleContainerMouseDown}
    >
      <div className={css.fixedHeader}>
        <div className={css.headerContent}>
          <div className={css.headerLeft}>
            <span className={css.headerTitle}>
              <FormattedMessage
                id="ui-plugin-inventory-templates.authority.suggestions.header"
                defaultMessage="Authority suggestions"
              />
            </span>
            {hasValidSuggestions && (
              <span className={css.resultsCount}>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.authority.results.count"
                  defaultMessage="Found {count} results"
                  values={{
                    count:
                      totalRecords > 0
                        ? totalRecords
                        : suggestions.length + (hasMore ? '+' : ''),
                  }}
                />
              </span>
            )}
          </div>
          <div className={css.headerRight}>
            {suggestions.length > 0 && (
              <>
                {facets?.sourceFileId &&
                  facets.sourceFileId.values &&
                  facets.sourceFileId.values.length > 0 && (
                    <SelectorComponent
                      className={css.sourceSelector}
                      label={
                        <FormattedMessage
                          id="ui-plugin-inventory-templates.authority.suggestions.sourceLabel"
                          defaultMessage="Source:"
                        />
                      }
                      id="suggestions-source-selector"
                      options={[
                        {
                          value: '',
                          label: (
                            <FormattedMessage
                              id="ui-plugin-inventory-templates.authority.suggestions.allSources"
                              defaultMessage="All sources"
                            />
                          ),
                        },
                        ...facets.sourceFileId.values.map(
                          ({ id, totalRecords }) => {
                            const sourceName =
                              id === 'NULL'
                                ? intl.formatMessage({
                                    id: 'ui-plugin-inventory-templates.authority.source.unspecified',
                                    defaultMessage: 'Unspecified',
                                  })
                                : getSourceFileNameById(id) || id;
                            return {
                              value: id,
                              label: `${sourceName} (${totalRecords})`,
                            };
                          },
                        ),
                      ]}
                      value={selectedSourceId}
                      onChange={(e) => handleSourceFilterChange(e.target.value)}
                    />
                  )}

                <SelectorComponent
                  className={css.countSelector}
                  label={
                    <FormattedMessage
                      id="ui-plugin-inventory-templates.authority.suggestions.showLabel"
                      defaultMessage="Show:"
                    />
                  }
                  id="suggestions-count-selector"
                  options={countOptions}
                  value={defaultSuggestionCount.toString()}
                  onChange={(e) =>
                    handleDefaultCountChange(parseInt(e.target.value, 10))
                  }
                />
              </>
            )}
          </div>
        </div>
      </div>

      <div className={css.contentArea}>
        {isLoading && (
          <div className={css.loadingContainer}>
            <Loading />
            <span className={css.loadingText}>
              <FormattedMessage
                id="ui-plugin-inventory-templates.authority.suggestions.loading"
                defaultMessage="Searching authority records..."
              />
            </span>
          </div>
        )}

        {error && (
          <div className={css.errorContainer}>
            <FormattedMessage
              id="ui-plugin-inventory-templates.authority.suggestions.error"
              defaultMessage="Error searching authority records: {error}"
              values={{ error }}
            />
          </div>
        )}

        {shouldShowNoResults && (
          <div className={css.noSuggestions}>
            <div className={css.noResultsMessage}>
              <FormattedMessage
                id="ui-plugin-inventory-templates.authority.suggestions.noResults"
                defaultMessage="No authority records found for '{searchTerm}'"
                values={{ searchTerm: textValue }}
              />
            </div>

            {shouldShowCreationButtonsInNoResults && (
              <div className={css.creationButtons}>
                <Button
                  buttonStyle="primary"
                  onMouseDown={(e) => handleMouseDownCreate(e, handleInstantCreate)}
                  onClick={handleInstantCreate}
                  disabled={isCreating}
                  data-testid="create-instant-authority"
                >
                  {isCreating ? (
                    <>
                      <Icon icon="spinner-ellipsis" />
                      <FormattedMessage
                        id="ui-plugin-inventory-templates.authority.creation.creating"
                        defaultMessage="Creating..."
                      />
                    </>
                  ) : (
                    <>
                      <Icon icon="plus-sign" />
                      <FormattedMessage
                        id="ui-plugin-inventory-templates.authority.creation.instant"
                        defaultMessage="Create and link authority"
                      />
                    </>
                  )}
                </Button>

                <Button
                  buttonStyle="default"
                  onMouseDown={(e) => handleMouseDownCreate(e, handleAdvancedCreate)}
                  onClick={handleAdvancedCreate}
                  disabled={isCreating}
                  data-testid="create-advanced-authority"
                >
                  <Icon icon="edit" />
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.authority.creation.advanced"
                    defaultMessage="Create with editor"
                  />
                </Button>
              </div>
            )}
          </div>
        )}

        {hasValidSuggestions && (
          <div className={css.suggestionsList}>
            {suggestions.map((suggestion, index) => (
              <SuggestionItem
                key={`${suggestion.id}-${index}`}
                suggestion={suggestion}
                onSelect={handleSelectSuggestion}
                getSourceFileNameById={getSourceFileNameById}
              />
            ))}
          </div>
        )}

        {hasMore && !isLoading && (
          <div className={css.loadMoreContainer}>
            <Button
              buttonStyle="primary"
              fullWidth
              onMouseDown={(e) => handleMouseDownCreate(e, loadMoreSuggestions)}
              onClick={loadMoreSuggestions}
              data-testid="load-more-suggestions"
            >
              <FormattedMessage
                id="ui-plugin-inventory-templates.authority.suggestions.loadMore"
                defaultMessage="Load {increment} more results"
                values={{ increment: getLoadMoreIncrement() }}
              />
            </Button>
          </div>
        )}
      </div>
      <Callout ref={calloutRef} />
    </div>
  );
}

AuthoritySuggestions.propTypes = propTypes;

export default AuthoritySuggestions;
